import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  PlusIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  PencilIcon,
  EyeIcon,
  CreditCardIcon,
  TrashIcon,
  ChartBarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { loanService, LoanType, LoanApplication, Loan } from '@/services/loanService'
import LoanApplicationModal from '@/components/LoanApplicationModal'
import LoanApplicationApprovalModal from '@/components/LoanApplicationApprovalModal'
import LoanPaymentModal from '@/components/LoanPaymentModal'
import LoanTypeModal from '@/components/LoanTypeModal'
import toast from 'react-hot-toast'

const Loans: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'overview' | 'applications' | 'loans' | 'types'>('overview')
  const [showLoanApplicationModal, setShowLoanApplicationModal] = useState(false)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showLoanTypeModal, setShowLoanTypeModal] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<LoanApplication | null>(null)
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null)
  const [selectedLoanType, setSelectedLoanType] = useState<LoanType | null>(null)

  // Fetch loan types
  const { data: loanTypesData, isLoading: loadingTypes, refetch: refetchTypes } = useQuery({
    queryKey: ['loanTypes'],
    queryFn: () => loanService.getLoanTypes(true),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch loan applications
  const { data: applicationsData, isLoading: loadingApplications, refetch: refetchApplications } = useQuery({
    queryKey: ['loanApplications'],
    queryFn: () => loanService.getLoanApplications(),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch active loans
  const { data: loansData, isLoading: loadingLoans, refetch: refetchLoans } = useQuery({
    queryKey: ['loans'],
    queryFn: () => loanService.getLoans(),
    staleTime: 5 * 60 * 1000,
  })

  const loanTypes: LoanType[] = loanTypesData?.data?.loanTypes || []
  const loanApplications: LoanApplication[] = applicationsData?.data?.loanApplications || []
  const activeLoans: Loan[] = loansData?.data?.loans || []

  const handleRefresh = async () => {
    try {
      await Promise.all([
        refetchTypes(),
        refetchApplications(),
        refetchLoans()
      ])
      toast.success('Data refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh data')
    }
  }

  const handleDeleteLoan = async (loan: Loan) => {
    if (!window.confirm(`Are you sure you want to delete the ${loan.loanType.name} loan for ${loan.employee.firstName} ${loan.employee.lastName}? This action cannot be undone.`)) {
      return
    }

    try {
      await loanService.deleteLoan(loan.id)
      toast.success('Loan deleted successfully')
      refetchLoans()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to delete loan'
      toast.error(errorMessage)
    }
  }

  const handleDeleteLoanType = async (loanType: LoanType) => {
    if (!window.confirm(`Are you sure you want to delete the "${loanType.name}" loan type? This action cannot be undone.`)) {
      return
    }

    try {
      await loanService.deleteLoanType(loanType.id)
      toast.success('Loan type deleted successfully')
      refetchTypes()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to delete loan type'
      toast.error(errorMessage)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
      case 'manager_approved': return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
      case 'hr_approved': return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
      case 'finance_approved': return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
      case 'approved': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
      case 'disbursed': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
      case 'rejected': return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
      case 'active': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
      case 'completed': return 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400'
      default: return 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getDashboardStats = () => {
    const totalApplications = loanApplications.length
    const pendingApplications = loanApplications.filter(app =>
      ['pending', 'manager_approved', 'hr_approved', 'finance_approved'].includes(app.status)
    ).length
    const totalActiveLoans = activeLoans.filter(loan => loan.status === 'active').length
    const totalOutstanding = activeLoans
      .filter(loan => loan.status === 'active')
      .reduce((sum, loan) => sum + Number(loan.outstandingBalance), 0)

    return { totalApplications, pendingApplications, totalActiveLoans, totalOutstanding }
  }

  const stats = getDashboardStats()

  const StatCard = ({ title, value, icon: Icon, iconColorClass, iconBgClass, trend }: {
    title: string;
    value: string | number;
    icon: React.ElementType;
    iconColorClass: string;
    iconBgClass: string;
    trend?: string;
  }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{title}</p>
          <p className="text-base font-bold text-gray-900 dark:text-white">{value}</p>
          {trend && <p className="text-xs text-emerald-600 mt-1">{trend}</p>}
        </div>
        <div className={`h-8 w-8 rounded flex items-center justify-center ${iconBgClass}`}>
          <Icon className={`h-4 w-4 ${iconColorClass}`} />
        </div>
      </div>
    </div>
  )

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Applications"
          value={stats.totalApplications}
          icon={DocumentTextIcon}
          iconColorClass="text-indigo-600 dark:text-indigo-400"
          iconBgClass="bg-indigo-50 dark:bg-indigo-900/20"
        />
        <StatCard
          title="Pending Applications"
          value={stats.pendingApplications}
          icon={ClockIcon}
          iconColorClass="text-amber-600 dark:text-amber-400"
          iconBgClass="bg-amber-50 dark:bg-amber-900/20"
        />
        <StatCard
          title="Active Loans"
          value={stats.totalActiveLoans}
          icon={UserGroupIcon}
          iconColorClass="text-emerald-600 dark:text-emerald-400"
          iconBgClass="bg-emerald-50 dark:bg-emerald-900/20"
        />
        <StatCard
          title="Total Outstanding"
          value={formatCurrency(stats.totalOutstanding)}
          icon={CurrencyDollarIcon}
          iconColorClass="text-indigo-600 dark:text-indigo-400"
          iconBgClass="bg-indigo-50 dark:bg-indigo-900/20"
        />
      </div>

      {/* Charts and Recent Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <ChartBarIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            Loan Distribution
          </h3>
          <div className="space-y-3">
            {loanTypes.slice(0, 4).map((type, index) => {
              const typeLoans = activeLoans.filter(loan => loan.loanType.id === type.id).length
              const percentage = loanTypes.length > 0 ? (typeLoans / activeLoans.length) * 100 : 0
              const barColors = ['bg-indigo-600', 'bg-emerald-500', 'bg-amber-500', 'bg-red-500']

              return (
                <div key={type.id} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{type.name}</span>
                    <span className="text-gray-500 dark:text-gray-400">{typeLoans} loans ({percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-500 ${barColors[index % barColors.length]}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent Activities</h3>
          {loanApplications.length === 0 ? (
            <div className="text-center py-6">
              <DocumentTextIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400 text-xs">No recent loan activities</p>
            </div>
          ) : (
            <div className="space-y-3">
              {loanApplications.slice(0, 5).map((application) => (
                <div key={application.id} className="flex items-start space-x-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-6 w-6 rounded flex items-center justify-center bg-indigo-50 dark:bg-indigo-900/20">
                      <DocumentTextIcon className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-white">
                      {application.employee.firstName} {application.employee.lastName}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      Applied for {application.loanType.name} - {formatCurrency(application.requestedAmount)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {formatDate(application.createdAt)}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(application.status)}`}>
                      {application.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderApplications = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Loan Applications</h2>
          <p className="text-gray-600 dark:text-gray-300 text-xs mt-1">Manage and review all loan applications</p>
        </div>
        {user?.role === 'employee' && (
          <button
            type="button"
            className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
            onClick={() => setShowLoanApplicationModal(true)}
          >
            <PlusIcon className="h-3 w-3" />
            Apply for Loan
          </button>
        )}
      </div>

      {loadingApplications ? (
        <div className="flex justify-center items-center py-8">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      ) : loanApplications.length === 0 ? (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <DocumentTextIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No applications found</h3>
          <p className="text-gray-500 dark:text-gray-400 text-xs mb-3">Get started by creating your first loan application</p>
          {user?.role === 'employee' && (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
              onClick={() => setShowLoanApplicationModal(true)}
            >
              <PlusIcon className="h-3 w-3" />
              Apply for Loan
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {loanApplications.map((application) => (
            <div key={application.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-card hover:shadow-md transition-all duration-200">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{application.loanType.name}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{application.employee.firstName} {application.employee.lastName}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(application.status)}`}>
                  {application.status.replace('_', ' ')}
                </span>
              </div>

              <div className="space-y-1.5 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Amount</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(application.requestedAmount)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Term</span>
                  <span className="font-medium text-gray-900 dark:text-white">{application.loanTermMonths} months</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Applied</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatDate(application.createdAt)}</span>
                </div>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                <strong>Purpose:</strong> {application.purpose}
              </p>

              {canApproveApplication(application) && (
                <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setSelectedApplication(application)
                      setShowApprovalModal(true)
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    <EyeIcon className="h-3 w-3" />
                    Review
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderLoans = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Active Loans</h2>
          <p className="text-gray-600 dark:text-gray-300 text-xs mt-1">Monitor and manage all active loans</p>
        </div>
      </div>

      {loadingLoans ? (
        <div className="flex justify-center items-center py-8">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      ) : activeLoans.length === 0 ? (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <BanknotesIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No active loans</h3>
          <p className="text-gray-500 dark:text-gray-400 text-xs">There are no active loans at the moment</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {activeLoans.map((loan) => (
            <div key={loan.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-card hover:shadow-md transition-all duration-200">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{loan.loanType.name}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{loan.employee.firstName} {loan.employee.lastName}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(loan.status)}`}>
                  {loan.status.toUpperCase()}
                </span>
              </div>

              <div className="space-y-1.5 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Principal</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(Number(loan.principalAmount))}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Outstanding</span>
                  <span className="font-medium text-xs text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(Number(loan.outstandingBalance))}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Monthly Payment</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(Number(loan.monthlyPayment))}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Payments Made</span>
                  <span className="font-medium text-gray-900 dark:text-white">{loan._count?.loanPayments || 0}</span>
                </div>
                {loan.nextPaymentDate && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Next Payment</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatDate(loan.nextPaymentDate)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-1">
                  {canRecordPayment() && loan.status === 'active' && (
                    <button
                      onClick={() => {
                        setSelectedLoan(loan)
                        setShowPaymentModal(true)
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors"
                    >
                      <CreditCardIcon className="h-3 w-3" />
                      Record Payment
                    </button>
                  )}
                </div>
                {canDeleteLoan() && ['pending', 'draft'].includes(loan.status) && (
                  <button
                    onClick={() => handleDeleteLoan(loan)}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    <TrashIcon className="h-3 w-3" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderTypes = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Loan Types</h2>
          <p className="text-gray-600 dark:text-gray-300 text-xs mt-1">Configure available loan products</p>
        </div>
        {canManageLoanTypes() && (
          <button
            type="button"
            className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
            onClick={() => {
              setSelectedLoanType(null)
              setShowLoanTypeModal(true)
            }}
          >
            <PlusIcon className="h-3 w-3" />
            Add Loan Type
          </button>
        )}
      </div>

      {loadingTypes ? (
        <div className="flex justify-center items-center py-8">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      ) : loanTypes.length === 0 ? (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <DocumentTextIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No loan types</h3>
          <p className="text-gray-500 dark:text-gray-400 text-xs mb-3">Start by creating your first loan type</p>
          {canManageLoanTypes() && (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
              onClick={() => {
                setSelectedLoanType(null)
                setShowLoanTypeModal(true)
              }}
            >
              <PlusIcon className="h-3 w-3" />
              Add Loan Type
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {loanTypes.map((loanType) => (
            <div key={loanType.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-card hover:shadow-md transition-all duration-200">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{loanType.name}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{loanType.description}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  loanType.isActive ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400'
                }`}>
                  {loanType.isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>

              <div className="space-y-1.5 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Interest Rate</span>
                  <span className="font-medium text-gray-900 dark:text-white">{loanType.interestRate}%</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">Min Amount</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(loanType.minAmount)}</span>
                </div>
                {loanType.maxAmount && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Max Amount</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(loanType.maxAmount)}</span>
                  </div>
                )}
                {loanType.maxTermMonths && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Max Term</span>
                    <span className="font-medium text-gray-900 dark:text-white">{loanType.maxTermMonths} months</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                <span>Applications: {loanType._count?.loanApplications || 0}</span>
                <span>Active Loans: {loanType._count?.loans || 0}</span>
              </div>

              <div className="flex justify-end space-x-1 pt-3 border-t border-gray-200 dark:border-gray-700">
                {canManageLoanTypes() && (
                  <button
                    onClick={() => {
                      setSelectedLoanType(loanType)
                      setShowLoanTypeModal(true)
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    <PencilIcon className="h-3 w-3" />
                    Edit
                  </button>
                )}
                {canDeleteLoanType() && (
                  <button
                    onClick={() => handleDeleteLoanType(loanType)}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    <TrashIcon className="h-3 w-3" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  // Helper functions for permissions (unchanged)
  const canApproveApplication = (application: LoanApplication) => {
    const { role } = user || {}
    const { status } = application

    if (role === 'admin') return true

    switch (status) {
      case 'pending':
        return role === 'manager'
      case 'manager_approved':
        return role === 'hr_manager'
      case 'hr_approved':
        return role === 'finance_manager'
      case 'finance_approved':
        return ['hr_manager', 'finance_manager'].includes(role || '')
      case 'approved':
        return ['admin', 'finance_manager'].includes(role || '')
      default:
        return false
    }
  }

  const canRecordPayment = () => {
    return user && ['admin', 'hr_manager', 'finance_manager'].includes(user.role)
  }

  const canManageLoanTypes = () => {
    return user && ['admin', 'hr_manager'].includes(user.role)
  }

  const canDeleteLoan = () => {
    return user && ['admin', 'hr_manager', 'finance_manager'].includes(user.role)
  }

  const canDeleteLoanType = () => {
    return user && user.role === 'admin'
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center">
            <BanknotesIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Loan Management</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Streamline loan applications, approvals, and tracking in one platform
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <ArrowPathIcon className="h-3 w-3" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-2">
        <nav className="flex space-x-1" aria-label="Tabs">
          {[
            { key: 'overview', name: 'Overview', icon: ChartBarIcon },
            { key: 'applications', name: 'Applications', icon: DocumentTextIcon },
            { key: 'loans', name: 'Active Loans', icon: BanknotesIcon },
            { key: 'types', name: 'Loan Types', icon: DocumentTextIcon },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`${
                activeTab === tab.key
                  ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-transparent'
              } flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border`}
            >
              <tab.icon className={`mr-1.5 h-3 w-3 ${activeTab === tab.key ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'applications' && renderApplications()}
        {activeTab === 'loans' && renderLoans()}
        {activeTab === 'types' && renderTypes()}
      </div>

      {/* Modals */}
      {showLoanApplicationModal && (
        <LoanApplicationModal
          onClose={() => setShowLoanApplicationModal(false)}
          onSuccess={() => {
            refetchApplications()
            refetchLoans()
          }}
        />
      )}

      {showApprovalModal && selectedApplication && (
        <LoanApplicationApprovalModal
          application={selectedApplication}
          onClose={() => {
            setShowApprovalModal(false)
            setSelectedApplication(null)
          }}
          onSuccess={() => {
            refetchApplications()
            refetchLoans()
          }}
        />
      )}

      {showPaymentModal && selectedLoan && (
        <LoanPaymentModal
          loan={selectedLoan}
          onClose={() => {
            setShowPaymentModal(false)
            setSelectedLoan(null)
          }}
          onSuccess={() => {
            refetchLoans()
          }}
        />
      )}

      {showLoanTypeModal && (
        <LoanTypeModal
          loanType={selectedLoanType}
          onClose={() => {
            setShowLoanTypeModal(false)
            setSelectedLoanType(null)
          }}
          onSuccess={() => {
            refetchTypes()
          }}
        />
      )}
    </div>
  )
}

export default Loans